import React, { useEffect, useMemo, useState } from "react";

import { useAdminFetch } from "../admin/hooks/useAdminFetch";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { Textarea } from "../ui/textarea";
import { STAKEHOLDER_URGENCY_OVERRIDE_OPTIONS } from "./config/stakeholderContactActions";
import { DAD_PARTNERS_DOCTRINE_FIELDS } from "./config/dadPartnersDoctrine";

const bucketOptions = [
  { value: "needs-now", label: "Needs Now" },
  { value: "moving", label: "Moving" },
  { value: "watch", label: "Watch" },
];

const SHARED_EDITABLE_FIELDS = [
  "bucket",
  "next_move",
  "notes",
  "follow_up_date",
  "last_meaningful_movement",
  "reason",
  "detail_tags",
  "contact_label",
];

const LANE_EDITABLE_FIELDS = {
  prospects: ["relationship_stage_label", "warmth_label", "dominant_pain_area", "source_label"],
  "dad-partners": [
    "relationship_stage_label",
    "dominant_pain_area",
    "children_age_label",
    ...DAD_PARTNERS_DOCTRINE_FIELDS,
  ],
  "guide-partners": ["guide_status_label", "invite_stage_label", "scheduled_date_label"],
  "support-partners": ["support_status_label"],
  "super-fans": ["relationship_label", "amplification_label"],
  "advisory-partners": ["advisory_signal_label"],
  operations: ["type"],
};

const LANE_FIELD_META = {
  relationship_stage_label: "Relationship stage",
  warmth_label: "Warmth",
  dominant_pain_area: "Dominant pain area",
  source_label: "Source",
  children_age_label: "Children age range",
  current_condition_note: "Current condition",
  home_proof_signal: "Home-visible proof",
  weekly_rhythm_signal: "Weekly rhythm signal",
  guide_status_label: "Guide status",
  invite_stage_label: "Invite stage",
  scheduled_date_label: "Scheduled date label",
  support_status_label: "Support status",
  relationship_label: "Relationship to mission",
  amplification_label: "Amplification signal",
  advisory_signal_label: "Advisory signal",
  type: "Operations type",
};

const parseTagString = (value) =>
  value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

export default function StakeholderDetailDrawer({ item, onClose, onSave }) {
  const adminFetch = useAdminFetch();
  const [draft, setDraft] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [savingSharedState, setSavingSharedState] = useState(false);

  useEffect(() => {
    setDraft(item ? { ...item, urgency_override: item.urgency_override || "auto" } : item);
    setSaveError("");
  }, [item]);

  const bucketLabel = useMemo(
    () => bucketOptions.find((option) => option.value === draft?.bucket)?.label || "Needs Now",
    [draft?.bucket]
  );

  if (!draft) {
    return (
      <Sheet onOpenChange={(open) => (!open ? onClose() : null)} open={false}>
        <SheetContent side="right" />
      </Sheet>
    );
  }

  const handleSave = async () => {
    const editableFields = [
      ...SHARED_EDITABLE_FIELDS,
      ...(LANE_EDITABLE_FIELDS[draft.lane] || []),
    ];
    let payload = editableFields.reduce((acc, key) => {
      if (draft[key] !== undefined) acc[key] = draft[key];
      return acc;
    }, {});

    setSavingSharedState(true);
    setSaveError("");
    try {
      const response = await adminFetch(`/admin/stakeholder/contacts/${encodeURIComponent(draft.id)}/state`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          follow_up_date: draft.follow_up_date || "",
          follow_up_type: draft.follow_up_type || "",
          follow_up_note: draft.follow_up_note || "",
          urgency_override: draft.urgency_override === "auto" ? "" : (draft.urgency_override || ""),
        }),
      });
      const sharedState = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(sharedState.detail || "Failed to save shared community state");
      }

      payload = {
        ...payload,
        follow_up_date: sharedState.follow_up_date ?? "",
        follow_up_type: sharedState.follow_up_type ?? "",
        follow_up_type_label: sharedState.follow_up_type_label ?? "",
        follow_up_note: sharedState.follow_up_note ?? "",
        urgency_override: sharedState.urgency_override ?? "",
        urgency_override_label: sharedState.urgency_override_label ?? "",
      };

      onSave(draft.id, payload);
      onClose();
    } catch (error) {
      setSaveError(error.message || "Failed to save shared community state");
    } finally {
      setSavingSharedState(false);
    }
  };

  const doctrineFields =
    draft.lane === "dad-partners" ? DAD_PARTNERS_DOCTRINE_FIELDS : [];
  const laneSpecificFields = (LANE_EDITABLE_FIELDS[draft.lane] || []).filter(
    (fieldName) => !doctrineFields.includes(fieldName)
  );

  return (
    <Sheet onOpenChange={(open) => (!open ? onClose() : null)} open={!!draft}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" side="right">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <SheetTitle>{draft.title}</SheetTitle>
            <Badge variant="outline" className="capitalize">
              {bucketLabel}
            </Badge>
            {draft.is_kevin_flagged ? <Badge>{draft.urgency_label || "Direct"}</Badge> : null}
          </div>
          <SheetDescription>
            {draft.lane.replace(/-/g, " ")} lane item. Update the operating truth here so the board and weekly report stay aligned.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Shared lane context
            </p>
            {draft.detail_tags?.length ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {draft.detail_tags.map((tag) => (
                  <Badge key={tag} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            ) : null}
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Best contact
                </label>
                <Input
                  className="mt-2"
                  onChange={(event) => setDraft((current) => ({ ...current, contact_label: event.target.value }))}
                  value={draft.contact_label || ""}
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Detail tags
                </label>
                <Input
                  className="mt-2"
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      detail_tags: parseTagString(event.target.value),
                    }))
                  }
                  value={(draft.detail_tags || []).join(", ")}
                />
              </div>
            </div>
          </div>

          {laneSpecificFields.length ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Lane-aware fields
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {laneSpecificFields.map((fieldName) => (
                  <div key={fieldName}>
                    <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      {LANE_FIELD_META[fieldName] || fieldName}
                    </label>
                    <Input
                      className="mt-2"
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, [fieldName]: event.target.value }))
                      }
                      value={draft[fieldName] || ""}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {doctrineFields.length ? (
            <div className="rounded-2xl border border-cyan-200 bg-cyan-50/70 p-4 dark:border-cyan-400/20 dark:bg-[#11161d]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-800 dark:text-cyan-300">
                Dad Partners doctrine
              </p>
              <div className="mt-4 space-y-4">
                {doctrineFields.map((fieldName) => (
                  <div key={fieldName}>
                    <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-200">
                      {LANE_FIELD_META[fieldName]}
                    </label>
                    <Textarea
                      className="mt-2 min-h-[84px] border-slate-300 bg-white text-slate-900 dark:border-white/10 dark:bg-[#0b1118] dark:text-slate-100"
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, [fieldName]: event.target.value }))
                      }
                      value={draft[fieldName] || ""}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Why this matters
            </p>
            <Textarea
              className="mt-3 min-h-[88px]"
              onChange={(event) => setDraft((current) => ({ ...current, reason: event.target.value }))}
              value={draft.reason || ""}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
              <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Bucket
              </label>
              <select
                className="mt-3 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 dark:border-white/10 dark:bg-[#11161d] dark:text-white"
                onChange={(event) => setDraft((current) => ({ ...current, bucket: event.target.value }))}
                value={draft.bucket}
              >
                {bucketOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
              <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Follow-up date
              </label>
              <Input
                className="mt-3"
                onChange={(event) =>
                  setDraft((current) => ({ ...current, follow_up_date: event.target.value }))
                }
                type="date"
                value={draft.follow_up_date || ""}
              />
              <button
                className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                onClick={() => setDraft((current) => ({ ...current, follow_up_date: "" }))}
                type="button"
              >
                Clear follow-up
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Urgency override
            </label>
            <select
              className="mt-3 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 dark:border-white/10 dark:bg-[#11161d] dark:text-white"
              onChange={(event) => setDraft((current) => ({ ...current, urgency_override: event.target.value }))}
              value={draft.urgency_override || "auto"}
            >
              {STAKEHOLDER_URGENCY_OVERRIDE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
              onClick={() => setDraft((current) => ({ ...current, urgency_override: "auto" }))}
              type="button"
            >
              Reset urgency
            </button>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Last meaningful movement
            </label>
            <Textarea
              className="mt-3 min-h-[84px]"
              onChange={(event) =>
                setDraft((current) => ({ ...current, last_meaningful_movement: event.target.value }))
              }
              value={draft.last_meaningful_movement || ""}
            />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Next move
            </label>
            <Input
              className="mt-3"
              onChange={(event) => setDraft((current) => ({ ...current, next_move: event.target.value }))}
              value={draft.next_move || ""}
            />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Stakeholder notes
            </label>
            <Textarea
              className="mt-3 min-h-[180px]"
              onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))}
              value={draft.notes || ""}
            />
          </div>

          {saveError ? (
            <p className="text-sm text-rose-600 dark:text-rose-300">{saveError}</p>
          ) : null}

          <div className="flex items-center justify-end gap-3 pb-8">
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button disabled={savingSharedState} onClick={handleSave}>
              Save operating truth
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
